import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from "@angular/common/http";
import { Injectable, Injector } from "@angular/core";
import { Observable, tap } from "rxjs";
import { LocalStorageKey } from "../../local-storage-key";
import { MaintenanceProvider } from "./maintenance";

/** サーバーが強制再起動を指示するレスポンスヘッダ(ビルド指定の強制再起動) */
export const FORCE_RELOAD_HEADER = "X-Ckuru-Force-Reload";
/** サーバーが強制再起動を指示するレスポンスヘッダ(ユーザー別の強制再起動) */
export const USER_FORCE_RELOAD_HEADER = "X-Ckuru-User-Force-Reload";

/** この画面(バンドル)を読み込んだ時刻(epoch秒)。これより後の指示だけを、この画面に向けたものとみなす */
const PAGE_LOADED_AT_SEC = Math.floor(Date.now() / 1000);

/**
 * 再起動するかの判定。
 * - 一度処理したタイムスタンプ以下は無視する(再読み込み後に同じ指示で繰り返さない)
 * - この画面を読み込む前に出された指示だけを対象にする(指示の後に開いた画面は既に最新)
 */
export const shouldForceReload = (
  timestamp: number,
  lastHandled: string | null,
  pageLoadedAtSec: number = PAGE_LOADED_AT_SEC
): boolean => {
  if (!Number.isFinite(timestamp) || timestamp <= 0) return false;
  if (lastHandled && Number(lastHandled) >= timestamp) return false;
  return timestamp > pageLoadedAtSec;
};

/**
 * APIレスポンスの強制再起動ヘッダを見て、確認ダイアログを出して画面を再読み込みする。
 * 古い画面を開いたままの端末を、追加のリクエストなしで最新に入れ替えるための仕組み
 * (アプリ復帰時の /maintenance による全体の強制再起動とは別経路。処理済みのタイムスタンプは共有する)。
 */
@Injectable()
export class ForceReloadInterceptor implements HttpInterceptor {
  // MaintenanceProvider は Api(HttpClient)に依存するため、コンストラクタで受けると循環依存になる。使う時に取り出す
  constructor(private injector: Injector) {}

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      tap((event) => {
        if (event instanceof HttpResponse) this.handle(event);
      })
    );
  }

  private handle(response: HttpResponse<unknown>): void {
    try {
      this.check(
        response.headers.get(FORCE_RELOAD_HEADER),
        LocalStorageKey.User.CkLastForceReloadTimestamp
      );
      this.check(
        response.headers.get(USER_FORCE_RELOAD_HEADER),
        LocalStorageKey.User.CkLastUserForceReloadTimestamp
      );
    } catch (e) {
      console.log(e);
    }
  }

  private check(headerValue: string | null, storageKey: string): void {
    if (!headerValue) return;
    const timestamp = Number(headerValue);
    const lastHandled = localStorage.getItem(storageKey);
    if (lastHandled && Number(lastHandled) >= timestamp) return;

    // 再起動するかに関わらず処理済みにする(同じ指示で何度も判定・再起動しない)
    localStorage.setItem(storageKey, String(timestamp));
    if (shouldForceReload(timestamp, lastHandled)) {
      this.injector.get(MaintenanceProvider).restart();
    }
  }
}
